import {Card, DataStatus, Form, FormField, Separator, FormSubmitHandler} from 'platform/components';
import {Grid, GridItem, Heading, Link, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {Trans} from 'react-i18next';

import {useGetWhatsAppConfigQuery, usePatchWhatsAppConfigMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

const whatsAppValidationSchema = z
  .object({
    enabled: z.boolean(),
    appId: z.string().max(255).optional().nullable(),
    appSecret: z.string().max(65535).optional().nullable(),
    wabaId: z.string().max(255).optional().nullable(),
    phoneNumber: z.string().max(255).optional().nullable(),
    permanentToken: z.string().max(65535).optional().nullable(),
    webhookVerificationToken: z.string().max(65535).optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.enabled) {
      const stringFields = [
        'appId',
        'appSecret',
        'wabaId',
        'phoneNumber',
        'permanentToken',
        'webhookVerificationToken',
      ] as const;

      type StringField = (typeof stringFields)[number];

      for (const field of stringFields) {
        const value = data[field as StringField];
        if (!value || value.trim().length === 0) {
          ctx.addIssue({
            path: [field],
            code: z.ZodIssueCode.too_small,
            type: 'string',
            minimum: 1,
            inclusive: true,
            message: i18n.t('general.errors.mixed.required'),
          });
        }
      }
    }
  });

type WhatsappValidationSchema = z.infer<typeof whatsAppValidationSchema>;

export function WhatsAppSettings() {
  const {data: whatsAppConfigData, isLoading: isGetWhatsAppConfigQueryLoading} =
    useGetWhatsAppConfigQuery();
  const [patchWhatsApp, {isLoading: isPatchWhatsAppConfigLoading}] =
    usePatchWhatsAppConfigMutation();

  const isLoading = isGetWhatsAppConfigQueryLoading || isPatchWhatsAppConfigLoading;

  const onSubmit: FormSubmitHandler<WhatsappValidationSchema> = async (
    values,
    _setErrors,
    reset
  ) => {
    const {enabled, ...rest} = values;
    await patchWhatsApp({
      whatsAppConfigRequestBody: {
        enabled,
        credentials: enabled
          ? {
              appId: rest?.appId ?? '',
              appSecret: rest?.appSecret ?? '',
              wabaId: rest?.wabaId ?? '',
              phoneNumber: rest?.phoneNumber ?? '',
              permanentToken: rest?.permanentToken ?? '',
              webhookVerificationToken: rest?.webhookVerificationToken ?? '',
            }
          : null,
      },
    })
      .unwrap()
      .then(() => {
        reset(undefined, {keepValues: true});
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isGetWhatsAppConfigQueryLoading}>
      <Form<WhatsappValidationSchema>
        mode="onSubmit"
        onSubmit={onSubmit}
        experimentalZodSchema={whatsAppValidationSchema}
        shouldWatchForUnsavedChanges
        defaultValues={{
          enabled: whatsAppConfigData?.enabled ?? false,
          appId: whatsAppConfigData?.credentials?.appId ?? '',
          appSecret: whatsAppConfigData?.credentials?.appSecret ?? '',
          wabaId: whatsAppConfigData?.credentials?.wabaId ?? '',
          phoneNumber: whatsAppConfigData?.credentials?.phoneNumber ?? '',
          permanentToken: whatsAppConfigData?.credentials?.permanentToken ?? '',
          webhookVerificationToken: whatsAppConfigData?.credentials?.webhookVerificationToken ?? '',
        }}
      >
        {(control, formApi) => {
          const isEnabled = formApi.watch('enabled');
          return (
            <VStack spacing={6}>
              <Text size="small">{i18n.t('page.settings.whatsApp.description1')}</Text>
              <Text size="small">{i18n.t('page.settings.whatsApp.description2')}</Text>
              <Text size="small">
                <Trans
                  i18nKey="page.settings.whatsApp.description3"
                  components={{
                    doclink: (
                      <Link
                        size="small"
                        href={i18n.t('page.settings.whatsApp.doclink')}
                        target="_blank"
                      />
                    ),
                  }}
                />
              </Text>
              <Card
                control={{
                  type: 'switch',
                  onChange: (value) => formApi.setValue('enabled', value),
                  value: isEnabled,
                }}
                title={i18n.t('page.settings.whatsApp.enableCommunication')}
                variant="inlineGrey"
                isExpanded={isEnabled}
              >
                <Grid columns={2}>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="wabaId"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.wabaId')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="phoneNumber"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.phoneNumber')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="appId"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.appId')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="appSecret"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.appSecret')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="permanentToken"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.permanentToken')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      isRequired
                      control={control}
                      name="webhookVerificationToken"
                      type="text"
                      label={i18n.t('page.settings.whatsApp.webhookVerificationToken')}
                    />
                  </GridItem>
                </Grid>
              </Card>
              <Separator />
              <Heading size={4}>{i18n.t('page.settings.whatsApp.conditions')}</Heading>
              <Text size="xSmall">{i18n.t('page.settings.whatsApp.description4')}</Text>
              <VStack spacing={1}>
                <Text size="xSmall" color="secondary">
                  {i18n.t('page.settings.whatsApp.paymentConditions')}
                </Text>
                <Heading size={4}>{i18n.t('page.settings.whatsApp.charge')}</Heading>
              </VStack>
              <SettingsFooter
                actions={[
                  {
                    type: 'form-button',
                    buttonType: 'submit',
                    variant: 'primary',
                    control,
                    title: i18n.t('general.actions.save'),
                    'data-testid': 'locale-config-submit',
                    isDisabled: isLoading,
                  },
                ]}
              />
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}
